import { FC } from 'react';
import { tailwindOverride, Table } from '@getsynapse/design-system';
import { Project } from 'types/store/projectsList';
import SkeletonLoader from 'Organisms/TableSkeletonLoader/PageTableLoader';
import ProjectsTableHeader from './ProjectsTableHeader/ProjectsTableHeader';
import ProjectsTableBody from './ProjectsTableBody';

interface ProjectsTableProps {
  isLoading?: boolean;
  tableName: string;
  projects: Project[];
  canFetchMoreProjects?: boolean;
  fetchMoreProjects: () => void;
  handleSort: (column: string) => void;
  hasAppliedFilters?: boolean;
  onAddProject: () => void;
  isConsolidatedView?: boolean;
  onOpenCancelModal?: (projectId: string) => void;
  onOpenOnHoldModal?: (projectId: string) => void;
  onOpenCloseModal?: (projectId: string) => void;
  onRefresh?: () => void;
}

const ProjectsTable: FC<ProjectsTableProps> = ({
  isLoading = false,
  projects,
  canFetchMoreProjects = false,
  hasAppliedFilters = false,
  fetchMoreProjects,
  tableName,
  handleSort,
  onAddProject,
  isConsolidatedView = false,
  onOpenCancelModal,
  onOpenOnHoldModal,
  onOpenCloseModal,
  onRefresh,
}) => {
  if (isLoading) {
    return <SkeletonLoader />;
  }

  const isTableEmpty = projects.length === 0;

  return (
    <div
      className={tailwindOverride(
        'w-full rounded-b border-neutral-lighter-two border-1 border-t-0',
        'overflow-auto',
        {
          'max-h-centralized-tasks-table': !hasAppliedFilters,
          'max-h-filtered-centralized-tasks-table': hasAppliedFilters,
          'overflow-hidden': isConsolidatedView && isTableEmpty,
        }
      )}
    >
      <Table
        className={tailwindOverride('w-full relative', {
          'max-w-full border-0': !isTableEmpty && !isConsolidatedView,
        })}
        canSelectRows={false}
        data={{
          headData: {
            headCells: [],
          },
          rows: [],
        }}
        aria-label={tableName}
      >
        <ProjectsTableHeader
          handleSort={handleSort}
          isTableEmpty={isTableEmpty}
          isConsolidatedView={isConsolidatedView}
        />
        <ProjectsTableBody
          tableName={tableName}
          projects={projects}
          canFetchMoreProjects={canFetchMoreProjects}
          fetchMoreProjects={fetchMoreProjects}
          onAddProject={onAddProject}
          isConsolidatedView={isConsolidatedView}
          onOpenCancelModal={onOpenCancelModal}
          onOpenOnHoldModal={onOpenOnHoldModal}
          onOpenCloseModal={onOpenCloseModal}
          onRefresh={onRefresh}
        />
      </Table>
    </div>
  );
};

export default ProjectsTable;
